import { RoleBasedFiltering } from './roleBasedFiltering';

/**
 * SLA Dashboard Access Control
 * 
 * Based on user story requirements:
 * - "Role-based access: Claims Officer, Approver, Finance" (Death claim user story)
 * - "Role-based access to maintain confidentiality" (Disability claim user story)
 * - "SLA tracking with visual dashboards" (Death claim user story)
 * 
 * Access levels:
 * - Full Access: Claims Officers, Approvers, Finance, Admin
 * - Limited Access: HR, Broker, CRM (only their own claims)
 * - No Access: Other roles
 */
export class SlaAccessControl extends RoleBasedFiltering {
  
  /**
   * Check if user can access SLA Dashboard
   * Based on user stories: Claims Officer, Approver, Finance have access
   */
  canAccessSlaDashboard() {
    // 🛠️ FIX: Restrict SLA Dashboard access to FULL access roles only
    // Remove limited roles (HR, Broker, CRM) from dashboard access
    const fullAccessRoles = ['Claims_Officer', 'Approver', 'Finance', 'Admin'];
    return this.hasAnyRole(fullAccessRoles);
  }

  /**
   * Check if user has full SLA dashboard access
   * Full access: Claims Officers, Approvers, Finance, Admin
   */
  hasFullSlaAccess() {
    return this.hasAnyRole(['Claims_Officer', 'Approver', 'Finance', 'Admin']);
  }

  /**
   * Check if user has limited SLA access (only their own claims)
   * Limited access: HR, Broker, CRM
   */
  hasLimitedSlaAccess() {
    const limitedRoles = ['HR', 'Broker', 'CRM'];
    const fullAccessRoles = ['Claims_Officer', 'Approver', 'Finance', 'Admin'];
    
    return this.hasAnyRole(limitedRoles) && !this.hasAnyRole(fullAccessRoles);
  }

  /**
   * Check if user can view SLA metrics and analytics
   * Only Claims Officers, Approvers, Finance, and Admin
   */
  canViewSlaMetrics() {
    return this.hasAnyRole(['Claims_Officer', 'Approver', 'Finance', 'Admin']);
  }

  /**
   * Check if user can trigger manual SLA checks
   * Only Claims Officers and Admin
   */
  canTriggerManualSlaCheck() {
    return this.hasAnyRole(['Claims_Officer', 'Admin']);
  }

  /**
   * Check if user can view escalated claims
   * Claims Officers, Approvers, Finance, Admin
   */
  canViewEscalatedClaims() {
    return this.hasAnyRole(['Claims_Officer', 'Approver', 'Finance', 'Admin']);
  }

  /**
   * Check if user can modify SLA settings (future feature)
   * Only Admin
   */
  canModifySlaSettings() {
    return this.hasRole('Admin');
  }

  /**
   * Get SLA dashboard sections visibility based on role
   */
  getSlaDashboardVisibility() {
    return {
      overview: this.canAccessSlaDashboard(),
      metrics: this.canViewSlaMetrics(),
      claimsList: this.canAccessSlaDashboard(),
      escalatedClaims: this.canViewEscalatedClaims(),
      manualCheck: this.canTriggerManualSlaCheck(),
      settings: this.canModifySlaSettings(),
      
      // Filter options
      filters: {
        allStatuses: this.hasFullSlaAccess(),
        allTypes: this.hasFullSlaAccess(),
        allUsers: this.hasFullSlaAccess()
      }
    };
  }

  /**
   * Get SLA query filters based on user role
   * Ensures users only see appropriate data
   */
  getSlaQueryFilters() {
    const filters = {};

    // If limited access, only show claims submitted by this user
    if (this.hasLimitedSlaAccess() && this.user?.id) {
      filters.submittedBy = this.user.id;
    }

    // Add other role-based filters as needed
    return filters;
  }

  /**
   * Check if user can view specific claim's SLA details
   */
  canViewClaimSla(claim) {
    // Full access roles can view any claim
    if (this.hasFullSlaAccess()) {
      return true;
    }

    // Limited access roles can only view their own claims
    if (this.hasLimitedSlaAccess()) {
      return claim.submittedBy?.id === this.user?.id;
    }

    return false;
  }

  /**
   * Get appropriate SLA dashboard message based on access level
   */
  getAccessLevelMessage() {
    if (this.hasFullSlaAccess()) {
      return {
        level: 'full',
        message: 'You have full access to SLA monitoring for all claims.',
        capabilities: [
          'View all claims SLA status',
          'Access SLA metrics and analytics', 
          'View escalated claims',
          'Trigger manual SLA checks'
        ]
      };
    }

    if (this.hasLimitedSlaAccess()) {
      return {
        level: 'limited',
        message: 'You can view SLA status for claims you have submitted.',
        capabilities: [
          'View SLA status for your submitted claims',
          'Track deadline compliance for your claims'
        ]
      };
    }

    return {
      level: 'none',
      message: 'You do not have access to SLA monitoring.',
      capabilities: []
    };
  }

  /**
   * Check if user should see SLA badges in claim lists
   */
  shouldShowSlaBadges() {
    // 🛠️ FIX: Show SLA badges to all authorized users, even if they can't access dashboard
    // Include both full access roles and limited access roles
    return this.hasAnyRole([
      'Claims_Officer',
      'Approver', 
      'Finance',
      'Admin',
      'HR',
      'Broker',
      'CRM'
    ]);
  }

  /**
   * Check if user can receive SLA notifications
   * Based on user stories: Claims Officers, Approvers get SLA alerts
   */
  canReceiveSlaNotifications() {
    return this.hasAnyRole(['Claims_Officer', 'Approver', 'Finance', 'Admin']);
  }

  /**
   * Get SLA notification preferences based on role
   */
  getSlaNotificationPreferences() {
    const preferences = {
      approachingBreach: false,
      breach: false,
      escalation: false
    };

    if (this.hasRole('Claims_Officer')) {
      preferences.approachingBreach = true;
      preferences.breach = true;
      preferences.escalation = true;
    }

    if (this.hasRole('Approver')) {
      preferences.breach = true;
      preferences.escalation = true;
    }

    if (this.hasRole('Finance')) {
      preferences.escalation = true; // Finance gets escalation notifications
    }

    if (this.hasRole('Admin')) {
      preferences.approachingBreach = true;
      preferences.breach = true;
      preferences.escalation = true;
    }

    return preferences;
  }
}

/**
 * Helper function to create SLA access control instance
 */
export const createSlaAccessControl = (user) => {
  return new SlaAccessControl(user?.roles || [], user);
};

/**
 * React hook for SLA access control
 */
export const useSlaAccess = (user) => {
  // 🛠️ FIX: Safely extract user roles from both nested and flat structures
  // Handle user.userRoles[].role.name (from Redux state) and user.roles (from JWT) formats
  const roles = 
    user?.userRoles?.map((ur) => ur.role?.name).filter(Boolean) ??
    user?.roles ??
    [];
    
  const slaAccess = new SlaAccessControl(roles, user);
  
  // Return the actual SlaAccessControl instance so methods can be called directly
  return {
    // Direct method access (for backward compatibility)
    canAccessSlaDashboard: () => slaAccess.canAccessSlaDashboard(),
    hasFullSlaAccess: () => slaAccess.hasFullSlaAccess(),
    hasLimitedSlaAccess: () => slaAccess.hasLimitedSlaAccess(),
    canViewMetrics: slaAccess.canViewSlaMetrics(),
    canTriggerManualSlaCheck: () => slaAccess.canTriggerManualSlaCheck(),
    canViewEscalatedClaims: () => slaAccess.canViewEscalatedClaims(),
    getSlaDashboardVisibility: () => slaAccess.getSlaDashboardVisibility(),
    getSlaQueryFilters: () => slaAccess.getSlaQueryFilters(),
    getAccessLevelMessage: () => slaAccess.getAccessLevelMessage(),
    shouldShowSlaBadges: () => slaAccess.shouldShowSlaBadges(),
    getSlaNotificationPreferences: () => slaAccess.getSlaNotificationPreferences(),
    
    // Alternative property access (new interface)
    canAccessDashboard: slaAccess.canAccessSlaDashboard(),
    hasFullAccess: slaAccess.hasFullSlaAccess(),
    hasLimitedAccess: slaAccess.hasLimitedSlaAccess(),
    canViewSlaMetrics: slaAccess.canViewSlaMetrics(),
    canTriggerCheck: slaAccess.canTriggerManualSlaCheck(),
    canViewEscalated: slaAccess.canViewEscalatedClaims(),
    dashboardVisibility: slaAccess.getSlaDashboardVisibility(),
    queryFilters: slaAccess.getSlaQueryFilters(),
    accessMessage: slaAccess.getAccessLevelMessage(),
    shouldShowBadges: slaAccess.shouldShowSlaBadges(),
    notificationPreferences: slaAccess.getSlaNotificationPreferences()
  };
};
